/**
 * REST helpers.
 *
 * The client is served by the same origin as the API, so there is no base URL
 * to configure and the teacher's httpOnly cookie rides along automatically.
 */

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string) {
    super(code);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const code = (body as { error?: string } | null)?.error ?? `http_${res.status}`;
    throw new ApiError(res.status, code);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /**
   * Uploads one file as its own raw bytes.
   *
   * Not JSON: base64 inside a JSON envelope costs a third more bandwidth and
   * would have to be decoded before it could be size-checked. The Content-Type
   * header is the declared type, which the server validates against a list.
   */
  upload: <T>(path: string, file: File, mime = file.type) =>
    request<T>(path, { method: 'POST', headers: { 'content-type': mime }, body: file }),
};

export interface Teacher { id: string; email: string; name: string }
export interface AuthPolicy { open: boolean; needsCode: boolean; first: boolean }
export interface AuthSession { signedIn: boolean; teacher: Teacher | null; policy: AuthPolicy }
export interface ClassRow {
  id: string; name: string; code: string; grade: number; created_at: string;
  students: number; live: boolean; curriculum_id: string | null;
}
/** What a student is shown before they type their name. */
export interface ClassInfoRow {
  code: string; className: string; teacherName: string; live: boolean;
}
export interface RosterRow {
  id: string; nickname: string; joined: string; score: number; matches: number;
  cleared: number; streak: number; badges: number; badgePoints: number; gradesAced: number[];
}
export interface AssetRow {
  id: string; kind: 'picture' | 'voice'; name: string;
  mime: string; bytes: number; created_at: string;
}
export interface MediaList { assets: AssetRow[]; usedBytes: number; quotaBytes: number }
export interface LeaderRow {
  student_id: string; nickname: string; total_score: number; total_matches: number;
  boards_cleared: number; day_streak: number; best_score: number; badges: number;
}
export interface PresetRow {
  id: string; name: string; grade: number; level: number; overrides: Record<string, unknown>;
}
